/**
 * Subscription API Client
 *
 * Stripe Subscription管理のためのAPIクライアント
 *
 * Issue #327 Phase 4: Subscription Update/Upgrade/Downgrade
 *
 * @module subscriptionClient
 */

import { apiClient } from '@/services/apiClient';

/**
 * Subscription Update Request Data
 */
export interface UpdateSubscriptionRequest {
  /** User ID */
  userId: string;
  /** New Plan ID (light, standard, premium) */
  newPlanId: string;
  /** Optional: Return URL after Stripe Checkout */
  returnUrl?: string;
}

/**
 * Subscription Summary (current/new plan info)
 */
export interface SubscriptionSummary {
  id: string;
  status: string;
  currentPlan: string;
  newPlan: string;
}

/**
 * Subscription Update Response Data
 *
 * Issue #3309 (PR-14): downgrade を期間末 schedule 経由に切替えたため、
 * downgrade 時は checkoutUrl=null + scheduledChangeAt=ISO 日時 を返す。
 * upgrade 時は従来通り checkoutUrl=Stripe Checkout URL を返す。
 */
export interface UpdateSubscriptionResponse {
  /** Stripe Checkout Session URL (upgrade のみ。downgrade 時は null) */
  checkoutUrl: string | null;
  /** 期間末 plan 切替予定日時 (ISO 8601、downgrade のみ。upgrade 時は null) */
  scheduledChangeAt: string | null;
  /** Subscription summary */
  subscription: SubscriptionSummary;
}

/**
 * Subscription API Client
 */
export const subscriptionClient = {
  /**
   * Update user's subscription plan
   *
   * Creates a Stripe Checkout Session for subscription upgrade/downgrade
   *
   * @param data - Update subscription request data
   * @returns Promise<UpdateSubscriptionResponse>
   *
   * @example
   * ```typescript
   * const response = await subscriptionClient.updateSubscription({
   *   userId: 'user_123',
   *   newPlanId: 'standard',
   * });
   * window.location.href = response.checkoutUrl;
   * ```
   */
  async updateSubscription(
    data: UpdateSubscriptionRequest
  ): Promise<UpdateSubscriptionResponse> {
    const response = await apiClient.post<UpdateSubscriptionResponse>(
      '/api/v1/subscriptions/update',
      data
    );
    return response.data;
  },
};
